import { css } from '@emotion/react'
import api from 'api'
import { ImpressaoAcompanhamentoMChatInput } from 'api/ImpressaoMChat'
import { Button, Heading, HFlow, Icon, ModalCloseButton, Text, VFlow } from 'bold-ui'
import { AccordionInformation } from 'components/accordion/AccordionInformation'
import { MChatPerguntaRespostaInput, MChatRiscoEnum } from 'graphql/types.generated'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { reportFormatDate, useHandleImpressao } from 'hooks/impressao/useHandleImpressao'
import { RefObject } from 'react'
import { emptyArrayOf } from 'util/array'

import { MChatRfResultadoAlert } from '../../components/MChatRfResultadoAlert'
import { MCHAT_RESULTADOS } from '../../model-mChatRf'
import { getAplicacaoStats } from '../consulta-seguimento-aplicacao/utils-consultaSeguimentoAplicacao'
import { ConsultaSeguimentoResultadoTable } from '../consulta-seguimento-resultado/ConsultaSeguimentoResultadoTable'
import { convertConsultaSeguimentoToInput } from '../converter-consultaSeguimento'
import { ConsultaSeguimentoFieldModel, ImpressaoConsultaSeguimento } from '../model-consultaSeguimento'
import { getResultadoLabel } from '../utils-consultaSeguimento'

interface ConsultaSeguimentoModalTabResultadoProps {
  value: ConsultaSeguimentoFieldModel | undefined
  backToAplicacaoButtonRef: RefObject<HTMLButtonElement>
  onGoBackToAplicacao: () => void
}

export function ConsultaSeguimentoModalTabResultado(props: Readonly<ConsultaSeguimentoModalTabResultadoProps>) {
  const { value, backToAplicacaoButtonRef, onGoBackToAplicacao } = props

  const {
    atendimentoProfissional: { id: atendProfId } = {},
    cidadao: { id: cidadaoId },
  } = useAtendimentoContext()

  const { analytics } = useFirebase()
  const { handleImpressaoPath, handleImpressaoError } = useHandleImpressao<ImpressaoAcompanhamentoMChatInput>()

  if (!value?.resultado) return null

  const { status, title, encaminhamento } = MCHAT_RESULTADOS[value.resultado]

  const aplicacaoStats = getAplicacaoStats(value.aplicacao)
  const aplicacaoRfToPrint = convertConsultaSeguimentoToInput(value) ?? emptyArrayOf<MChatPerguntaRespostaInput>()

  const handlePrint = () => {
    const input: ImpressaoConsultaSeguimento = {
      atendProfId: atendProfId,
      cidadaoId: cidadaoId,
      totalPerguntasRespondidas: aplicacaoStats.totalPerguntasRespondidas,
      totalPerguntasFalhou: aplicacaoStats.totalPerguntasFalhou,
      totalPerguntasPassou: aplicacaoStats.totalPerguntasPassou,
      resultadoSeguimento: value.resultado,
      aplicacaoRf: aplicacaoRfToPrint,
    }

    handleImpressaoPath({
      apiPath: api.mchat.imprimirMChat,
      values: input,
      file: `MChat.pdf`,
      dateFormat: reportFormatDate,
    }).catch(handleImpressaoError)
  }

  return (
    <VFlow>
      <VFlow vSpacing={2}>
        <HFlow alignItems='flex-start' justifyContent='space-between'>
          <Button
            kind='normal'
            size='small'
            innerRef={backToAplicacaoButtonRef}
            onClick={() => {
              analytics.logEvent('click_editar_mchatrf')
              onGoBackToAplicacao()
            }}
          >
            <Icon icon='arrowLeft' style={styles.iconMargin} />
            Editar avaliação
          </Button>
          <ModalCloseButton />
        </HFlow>
        <VFlow>
          <Heading level={1}>Resultado Entrevista de seguimento</Heading>
          <HFlow alignItems='flex-start' justifyContent='space-between'>
            <VFlow vSpacing={0.175}>
              <Heading level={2} color={value.resultado === MChatRiscoEnum.NEGATIVO ? 'success' : 'danger'}>
                {title}
              </Heading>
              <Text fontWeight='bold'>{getResultadoLabel(aplicacaoStats)}</Text>
            </VFlow>
            <Button kind='primary' skin='outline' size='small' style={styles.printButton} onClick={handlePrint}>
              <Icon icon='printerOutline' style={styles.iconMargin} />
              Imprimir resultado
            </Button>
          </HFlow>
          <AccordionInformation uuid='resultado' type='normal' header='Como é calculado o resultado?'>
            <Text>
              A entrevista é considerada como sendo de rastreamento positivo se a criança falhar em dois itens quaisquer
              da entrevista de seguimento. Se a criança for rastreada positivamente no M-CHAT-R/F, é altamente
              recomendado que a criança seja encaminhada para a intervenção precoce e testagem diagnóstica o mais breve
              possível. Atenção, se o agente de saúde ou o familiar tiver preocupações sobre TEA, a criança deve ser
              encaminhada para avaliação, independente da pontuação no M-CHAT-R ou M-CHAT-R/F.
            </Text>
          </AccordionInformation>
          <VFlow vSpacing={0.5}>
            <Text fontWeight='bold'>Encaminhamento</Text>
            <MChatRfResultadoAlert status={status}>{encaminhamento()}</MChatRfResultadoAlert>
          </VFlow>
          <ConsultaSeguimentoResultadoTable aplicacao={value.aplicacao} />
        </VFlow>
      </VFlow>
    </VFlow>
  )
}

const styles = {
  iconMargin: css`
    margin-right: 0.5rem;
  `,
  printButton: css`
    margin-top: 0.25rem;
  `,
}
